/* File: frontend/src/components/reputation/ContentWarfarePanel.js */
/* Component for Content Warfare Panel */
/* Version: v1.1 (Time Estimate Update) */
/* Date: 2025-12-15 19:30 EST */
/* Description: 
   - Вынесена панель управления "Content Warfare".
   - Включает блок генерации статей (Start Attack) и блок Schema.org.
   - Содержит поле ввода Custom AI Instructions.
   - Отображает моментальный статус генерации.
   - v1.1: Обновлено время ожидания под кнопкой Start Attack на "15-45 мин" и улучшено позиционирование.
*/

import React from 'react';
import { Shield, Zap, RefreshCw, Code, CheckCircle, Edit3 } from 'lucide-react';


const ContentWarfarePanel = ({
    profile, customPrompt, onPromptChange, onAttack,
    onGenerateSchema, // [FIX] Renamed from onSchema to match parent (ReputationProfileEditor)
    loadingArticle, loadingSchema, articleResult, schemaResult, onCopy
}) => {
    // [FIX] Restore UI Visibility: If profile is missing, show panel but disable actions (User Feedback)
    const isReady = !!profile;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start', opacity: isReady ? 1 : 0.6, pointerEvents: isReady ? 'auto' : 'none' }}>
            {!isReady && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{ background: '#fff', padding: '12px 24px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', color: '#64748b', fontWeight: '500' }}>
                        Select a location to unlock Content Warfare
                    </div>
                </div>
            )}


            {/* COLUMN 1: CORNERSTONE CONTENT */}
            <div className="ui-card">
                <div className="ui-card-header" style={{ color: '#b45309', borderBottomColor: '#fef3c7' }}>
                    <Zap size={16} className="text-yellow-600" /> HYPER-LOCAL CORNERSTONE
                </div>

                <div style={{ fontSize: '13px', color: '#78350f', background: '#fffbeb', padding: '12px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                    Win Rate is <strong>Low</strong>. AI models lack authoritative data linking your firm to <strong>{profile?.city || "this area"}</strong>.
                </div>

                <div>
                    <label className="ui-label" style={{ color: '#b45309' }}>Custom Instructions <Edit3 size={10} style={{ display: 'inline', marginLeft: 4 }} /></label>
                    <textarea
                        className="ui-input"
                        name="custom_prompt"
                        value={customPrompt}
                        onChange={onPromptChange}
                        placeholder="E.g., 'Focus on Probate in Queens', 'Use aggressive tone'..."
                        rows={3}
                        style={{ borderColor: '#fde68a' }}
                    />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                    <div style={{ fontSize: '11px', color: '#92400e', display: 'flex', gap: '12px' }}>
                        <span>Target: <strong>{profile?.city || "Local"}</strong></span>
                        <span>Model: <strong>Gemini Pro 2.5 Flash</strong></span>
                    </div>

                    <button
                        onClick={() => {
                            if (isReady && profile && profile.id) {
                                onAttack(profile.id);
                            } else {
                                console.warn('[ContentWarfare] Cannot attack: Missing profile ID');
                            }
                        }}
                        disabled={loadingArticle || !isReady}
                        className="btn-primary"
                        style={{
                            background: loadingArticle ? '#d97706' : '#b45309', // Slight visual cue for loading
                            borderColor: '#b45309',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: (loadingArticle || !isReady) ? 'not-allowed' : 'pointer',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: (loadingArticle || !isReady) ? 0.7 : 1
                        }}
                    >
                        {loadingArticle ? <RefreshCw size={14} className="spin" /> : <Zap size={14} />}
                        {loadingArticle ? 'Attacking...' : 'Start Attack'}
                    </button>
                </div>

                {articleResult && (
                    <div style={{ padding: '10px 12px', background: '#ecfdf5', borderRadius: '6px', border: '1px solid #d1fae5', fontSize: '12px', color: '#065f46', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle size={14} /> Operation launched. Content will appear in "Feed".
                    </div>
                )}
            </div>

            {/* COLUMN 2: SCHEMA SNIPER */}
            <div className="ui-card">
                <div className="ui-card-header" style={{ color: '#1e40af', borderBottomColor: '#dbeafe' }}>
                    <Code size={16} className="text-blue-600" /> SCHEMA.ORG SNIPER
                </div>

                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                    Generate JSON-LD to force entity authority and knowledge graph connection.
                </p>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                    <button
                        onClick={() => {
                            if (isReady && onGenerateSchema) {
                                onGenerateSchema();
                            } else {
                                console.warn('[ContentWarfare] Cannot generate schema: Not ready or handler missing');
                            }
                        }}
                        disabled={loadingSchema || !isReady}
                        className="btn-primary"
                        style={{
                            background: '#2563eb',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: (loadingSchema || !isReady) ? 'not-allowed' : 'pointer',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: (loadingSchema || !isReady) ? 0.7 : 1
                        }}
                    >
                        {loadingSchema ? <RefreshCw size={14} className="spin" /> : <Code size={14} />}
                        {loadingSchema ? 'Processing...' : 'Generate JSON-LD'}
                    </button>
                </div>

                {schemaResult && (
                    <div style={{ marginTop: '12px', position: 'relative' }}>
                        <div style={{
                            background: '#0f172a', color: '#f8fafc', padding: '12px', borderRadius: '6px',
                            fontSize: '11px', fontFamily: 'monospace', overflowX: 'auto', maxHeight: '200px'
                        }}>
                            <pre style={{ margin: 0 }}>{schemaResult}</pre>
                        </div>
                        <button onClick={() => onCopy(schemaResult)} style={{
                            position: 'absolute', top: '8px', right: '8px', padding: '4px 8px', background: 'rgba(255,255,255,0.1)',
                            color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', cursor: 'pointer'
                        }}>
                            Copy
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContentWarfarePanel;